"use client";

import type { SkillCatalogEntry } from "@/types/skills";

import React from "react";
import {
  Button,
  Tooltip,
  TooltipTrigger,
  TooltipContent,
  Switch,
  Spinner,
  Popover,
  PopoverTrigger,
  PopoverContent,
} from "@heroui/react";
import { Icon } from "@iconify/react";
import { cn } from "@heroui/react";
import { useTranslation } from "react-i18next";
import { useNavigate } from "react-router-dom";
import { useBrand } from "@webuild-ai/react-ui/hooks";

import { logger } from "@/telemetry";
import { useCapabilities } from "@/hooks/useCapabilities";
import { useTheme } from "@/hooks/use-theme";
import { blurActiveElement } from "@/utils/dom";
import { getReadableTextColor } from "@/utils/color";
import { useUIPreferences } from "@/stores/UIPreferencesStore";
import { useWorkflowStore } from "@/stores/WorkflowStore";
import { useToolPreferencesStore } from "@/stores/ToolPreferencesStore";
import { useChatStore } from "@/stores/ChatStore";
import { useIntegrationOAuth } from "@/hooks/useIntegrationOAuth";
import { useWorkflowList } from "@/hooks/chat/useWorkflowList";
import { useWebuildAPIClient } from "@/providers/WeBuildClientProvider";
import { siteConfig } from "@/config/site";
import { SERVICES } from "@/config/integrations";

import { ChatCommandMenu, SkillCommandEntry } from "./ChatCommandMenu";
import { ChatModelSelector } from "./ChatModelSelector";

export interface ChatInputActionsProps {
  /** Current prompt text — used to determine send-button state. */
  prompt: string;
  /** Whether the AI response is currently streaming. */
  streaming: boolean;
  /** Whether a file upload request is in progress. */
  isUploading: boolean;
  /** Whether uploaded files are still being processed by the backend. */
  isUploadingFiles: boolean;
  /** Whether the prompt input is locked (pre-filled workflow default). */
  isPromptLocked: boolean;
  /** Whether web search is currently enabled. */
  isSearchEnabled: boolean;
  /** Whether Knowledge Hub is currently enabled. */
  isKnowledgeHubEnabled: boolean;
  /** Whether the configuration panel is disabled (specialised workflow or active chat). */
  isConfigurationDisabled: boolean;
  /** Whether voice recording is active. */
  listening: boolean;
  /** Whether the current browser supports the Web Speech API. */
  browserSupportsSpeechRecognition: boolean;
  /** Non-null when there is a speech-recognition error to surface. */
  speechError: string | null;

  // ── Visibility (permission gates — always respected) ────────────────
  showFileUpload: boolean;
  showVoice: boolean;
  showSearch: boolean;
  showKnowledgeHub: boolean;
  showPrompts: boolean;
  showPrompto: boolean;
  showSettings: boolean;
  showChatModelSelector: boolean;

  // ── UI preferences ───────────────────────────────────────────────────
  /** When true, only pinned controls are shown inline; unpinned ones live in the menus. */
  simpleChatInput: boolean;
  /** Per-control pinned state for the chat input toolbar. */
  pinnedChatControls: {
    prompts: boolean;
    prompto: boolean;
    search: boolean;
    knowledgeHub: boolean;
    configuration: boolean;
    modelSelector: boolean;
  };

  // ── Command menu state ───────────────────────────────────────────────
  commandMenuVisible: boolean;
  commandMenuPosition: { top: number; left: number };
  /** Increment to request opening the "+" add menu (e.g. the user typed "+"). */
  openAddMenuSignal?: number;

  // ── Handlers ────────────────────────────────────────────────────────
  onFileUploadClick: () => void;
  onVoicePress: () => void;
  onWebSearchToggle: (val: boolean) => void;
  onKnowledgeHubToggle: () => void;
  onBrowsePrompts: () => void;
  onPrompto: () => void;
  onOpenConfiguration: () => void;
  onCommandMenuOpen: () => void;
  onCommandMenuClose: () => void;
  onStop: () => void;
  /** Called when the user activates the "Mention people or workflows" item in the add menu. */
  onMention: () => void;
  /** Called with the skill's catalogue id when a skill row is chosen from the command menu. */
  onSelectSkill: (skillId: string) => void;

  // ── Refs ─────────────────────────────────────────────────────────────
  fileInputRef: React.RefObject<HTMLInputElement>;

  // ── File input element props ─────────────────────────────────────────
  allowedFileTypes: string[];
  onFileChange: (event: React.ChangeEvent<HTMLInputElement>) => void;
}

/**
 * ChatInputActions
 *
 * Shared action row for the chat composer, rendered at the bottom of the
 * PromptWithActions form. Provides:
 *   - Bottom-left: "+" add menu (attach file / mention), "/" slash affordance,
 *     and any inline controls (pinned or advanced-mode).
 *   - Bottom-right: active-feature chips (gated by showActiveFeatureChips pref),
 *     model selector, voice microphone, send / stop button.
 *   - A hidden file <input> and a ChatCommandMenu portal.
 *
 * The helper-line hint ("Type / for commands…") is intentionally rendered
 * OUTSIDE this component by the parent, below the <form> element.
 */
export const ChatInputActions = ({
  prompt,
  streaming,
  isUploading,
  isUploadingFiles,
  isPromptLocked,
  isSearchEnabled,
  isKnowledgeHubEnabled,
  isConfigurationDisabled,
  listening,
  browserSupportsSpeechRecognition,
  speechError,
  showFileUpload,
  showVoice,
  showSearch,
  showKnowledgeHub,
  showPrompts,
  showPrompto,
  showSettings,
  showChatModelSelector,
  simpleChatInput,
  pinnedChatControls,
  commandMenuVisible,
  commandMenuPosition,
  openAddMenuSignal,
  onFileUploadClick,
  onVoicePress,
  onWebSearchToggle,
  onKnowledgeHubToggle,
  onBrowsePrompts,
  onPrompto,
  onOpenConfiguration,
  onCommandMenuOpen,
  onCommandMenuClose,
  onStop,
  onMention,
  onSelectSkill,
  fileInputRef,
  allowedFileTypes,
  onFileChange,
}: ChatInputActionsProps) => {
  const { t } = useTranslation();
  const { brand } = useBrand();
  const { isDark } = useTheme();
  const navigate = useNavigate();
  const { authenticatedFetch } = useWebuildAPIClient();
  const { showActiveFeatureChips, setPinnedChatControl } = useUIPreferences();
  const { workflow, updateAgentTools } = useWorkflowStore();
  const { runId } = useChatStore();
  const { ensureToolConnected, connectingToolId, prefetchStatuses, getToolConnectionStatus } =
    useIntegrationOAuth();
  const { canCreateWorkflows } = useCapabilities();
  // Workflows the user can switch to, surfaced as a section in the command menu.
  const { workflows } = useWorkflowList();

  // Brand-localised heading for the Workflows section (e.g. NGV renders its own
  // label), matching how the sidebar names the Workflows area.
  const workflowsLabel = brand.copy.menuItems?.["Workflows"];

  // ── Real skills catalogue for the command menu's Skills section ────────
  // Fetched once the command menu first opens (server already filters by the
  // user's access). Only active or global skills are surfaced — the assistant
  // cannot use a disabled tenant skill.
  const [skills, setSkills] = React.useState<SkillCommandEntry[]>([]);
  const skillsFetchedRef = React.useRef(false);

  React.useEffect(() => {
    if (!commandMenuVisible || skillsFetchedRef.current || !authenticatedFetch) return;
    skillsFetchedRef.current = true;

    const baseUrl = siteConfig().adminAPIURL.replace(/\/$/, "");

    authenticatedFetch(`${baseUrl}/admin/v1/skills/catalog`)
      .then(async (response) => {
        if (!response.ok) {
          logger.error("Failed to fetch skills catalogue", { status: response.status });

          return;
        }

        const data: SkillCatalogEntry[] = await response.json();

        setSkills(
          data
            .filter((s) => s.is_global || s.is_active)
            .map((s) => ({
              id: s.id,
              name: s.name,
              description: s.description,
              icon: s.icon || "solar:magic-stick-3-linear",
            })),
        );
      })
      .catch((error) => {
        // Allow a later open to retry if the request failed outright.
        skillsFetchedRef.current = false;
        logger.error("Error fetching skills catalogue", { error: String(error) });
      });
  }, [commandMenuVisible, authenticatedFetch]);

  /**
   * Switch the chat to a different workflow. Reuses the existing
   * /workflows/:id route (the same additive path the Prompto button uses), so
   * there is no bespoke "attach workflow" call. Navigating starts a fresh chat
   * with that workflow; the previous chat is preserved in chat history.
   */
  const handleSelectWorkflow = (workflowId: string) => {
    logger.info("ChatInputActions: workflow selected", { workflowId });
    onCommandMenuClose();
    navigate(`/workflows/${workflowId}`);
  };

  /**
   * "Browse all workflows" footer action: open the Library on its Workflows
   * tab, which replaced the standalone Workflows index (ADR-0019).
   */
  const handleBrowseAllWorkflows = () => {
    logger.info("ChatInputActions: browse all workflows");
    onCommandMenuClose();
    navigate("/library?tab=workflow");
  };

  /**
   * "New workflow" footer action: open the Workflow Builder on a new workflow.
   * Gated on `canCreateWorkflows` from the shared capability layer, so this
   * action, the Library's "+ New" menu and the Library's Clone action all agree.
   * Checking `workflows:create` alone here missed the `workflows:admin` grant.
   */
  const handleCreateWorkflow = () => {
    logger.info("ChatInputActions: create new workflow");
    onCommandMenuClose();
    navigate("/workflow-builder");
  };

  // ── Popover open state for the "+" add menu ──────────────────────────
  const [addMenuOpen, setAddMenuOpen] = React.useState(false);
  // Which panel the add menu is showing: the root list or the connectors sub-view.
  const [addMenuView, setAddMenuView] = React.useState<"main" | "connectors">("main");

  // Open the add menu (root view) when the parent signals it — e.g. the user
  // typed "+" in the composer, the keyboard equivalent of clicking the + button.
  React.useEffect(() => {
    if (openAddMenuSignal) {
      setAddMenuView("main");
      setAddMenuOpen(true);
    }
  }, [openAddMenuSignal]);

  // ── User Memory state (mirrors WorkflowSettings: the chat agent's
  //    `save_to_memory` tool). Surfaced here so it can be toggled from the
  //    add menu without opening the full configuration panel. ────────────
  const chatAgentEntry = React.useMemo(() => {
    if (!workflow?.agents) return null;

    return Object.entries(workflow.agents).find(([, agent]) => agent.type === "chat_agent") ?? null;
  }, [workflow]);

  const isMemoryEnabled = React.useMemo(() => {
    if (!chatAgentEntry) return false;
    const tools: string[] = chatAgentEntry[1]?.parameters?.tools ?? [];

    return tools.includes("save_to_memory");
  }, [chatAgentEntry]);

  const handleMemoryToggle = (val: boolean) => {
    if (!chatAgentEntry || !workflow) return;
    const [agentId] = chatAgentEntry;

    updateAgentTools(agentId, "save_to_memory", val);

    // Persist full tool list to ToolPreferencesStore (localStorage).
    const updatedWorkflow = useWorkflowStore.getState().workflow;
    const currentTools = updatedWorkflow?.agents[agentId]?.parameters?.tools || [];

    useToolPreferencesStore.getState().setAgentTools(workflow.id, agentId, currentTools);
  };

  /** Open the connectors sub-view and warm the integration status cache. */
  const handleOpenConnectors = () => {
    logger.info("ChatInputActions: connectors view opened");
    setAddMenuView("connectors");
    prefetchStatuses();
  };

  /** Connect a connector via the shared OAuth popup flow (reused from ToolPicker). */
  const handleConnectorPress = (serviceId: string) => {
    logger.info("ChatInputActions: connector connect pressed", { serviceId });
    ensureToolConnected(serviceId);
  };

  /** Reset to the root view whenever the add menu closes. */
  const handleAddMenuOpenChange = (open: boolean) => {
    setAddMenuOpen(open);
    if (!open) setAddMenuView("main");
  };

  /** "Manage connectors" navigates to the integrations settings page. */
  const handleManageConnectors = () => {
    setAddMenuOpen(false);
    setAddMenuView("main");
    navigate("/settings#integrations");
  };

  /**
   * Determines whether a named control should be rendered inline.
   *
   * Permission gates (`permissionAllows`) are always respected regardless of
   * mode. In advanced mode all permitted controls are shown; in simple mode
   * only pinned controls appear inline (unpinned ones live in the menus).
   */
  const isControlVisible = (
    key: keyof typeof pinnedChatControls,
    permissionAllows: boolean,
  ): boolean => {
    if (!permissionAllows) return false;
    if (!simpleChatInput) return true; // advanced mode: show all

    return pinnedChatControls[key]; // simple mode: only pinned
  };

  /** Brand-coloured inline style for the send button when a prompt is ready. */
  const sendPrimary = isDark ? brand.colours.dark.primary : brand.colours.light.primary;
  // Contrast-safe foreground derived from the brand primary (no brand foreground
  // token exists), so a light brand primary still reads. The icon inherits this
  // via currentColor.
  const sendForeground = React.useMemo(() => getReadableTextColor(sendPrimary), [sendPrimary]);
  const sendButtonStyle: React.CSSProperties | undefined =
    !streaming && prompt
      ? {
          backgroundColor: sendPrimary,
          color: sendForeground,
          boxShadow: `0 2px 8px -2px color-mix(in srgb, ${sendPrimary} 33%, transparent)`,
        }
      : undefined;

  /** Handle "Attach file" press: trigger the hidden file input and close the menu. */
  const handleAttachFile = () => {
    logger.info("ChatInputActions: attach file pressed");
    setAddMenuOpen(false);
    // Small delay so the popover has time to dismiss before the file dialog opens.
    setTimeout(() => {
      onFileUploadClick();
      blurActiveElement();
    }, 0);
  };

  /** "Mention people or workflows" closes the add menu then delegates to the parent handler. */
  const handleMention = () => {
    logger.info("ChatInputActions: mention pressed");
    setAddMenuOpen(false);
    onMention();
  };

  /** Open chat-input settings from the add-menu footer cog. */
  const handleOpenChatInputSettings = () => {
    setAddMenuOpen(false);
    setAddMenuView("main");
    navigate("/settings#general");
  };

  /**
   * ArrowUp/Down roving focus across the add menu's action rows so the footer's
   * "Navigate" hint is accurate. A document-level listener (only while the menu
   * is open) is used because HeroUI's PopoverContent does not forward onKeyDown
   * and focus may sit on the dialog container rather than on an item. Toggle
   * rows (Memory) are excluded and stay Tab-reachable, mirroring the command
   * menu's keyboard model.
   */
  React.useEffect(() => {
    if (!addMenuOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key !== "ArrowDown" && e.key !== "ArrowUp") return;

      const items = Array.from(
        document.querySelectorAll<HTMLButtonElement>("[data-add-menu-nav]:not([disabled])"),
      );

      if (items.length === 0) return;
      e.preventDefault();

      const currentIndex = items.findIndex((el) => el === document.activeElement);
      const delta = e.key === "ArrowDown" ? 1 : -1;
      const nextIndex =
        currentIndex === -1
          ? e.key === "ArrowDown"
            ? 0
            : items.length - 1
          : (currentIndex + delta + items.length) % items.length;

      items[nextIndex]?.focus();
    };

    // Capture phase so the handler runs even if HeroUI's popover stops
    // propagation of the key event before it bubbles to the document.
    document.addEventListener("keydown", handleKeyDown, true);

    return () => document.removeEventListener("keydown", handleKeyDown, true);
  }, [addMenuOpen]);

  return (
    <div
      className="relative z-10 flex w-full flex-col gap-2 px-4 pb-4 shrink-0 bg-default-50 dark:bg-content2 rounded-b-2xl"
      data-id="prompt-actions-container"
    >
      {/* ── Main action row ──────────────────────────────────────────────── */}
      <div className="flex w-full items-center justify-between gap-2">
        {/* LEFT: "+" add menu, "/" slash affordance, then any inline controls */}
        <div className="flex items-center gap-2 flex-wrap">
          {/* "+" Add menu — shown when file upload is permitted */}
          {showFileUpload && (
            <Popover
              isOpen={addMenuOpen}
              placement="top-start"
              onOpenChange={handleAddMenuOpenChange}
            >
              <PopoverTrigger>
                <Button
                  isIconOnly
                  aria-label={t("chatInput.addMenu.label")}
                  className="rounded-full border border-default-300 bg-default-100 hover:bg-default-200 focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
                  data-id="add-menu-button"
                  size="sm"
                  variant="ghost"
                >
                  <Icon icon="solar:add-circle-linear" width={18} />
                </Button>
              </PopoverTrigger>
              <PopoverContent className="min-w-60 overflow-hidden rounded-xl border border-divider outline-none">
                {addMenuView === "main" ? (
                  <div className="flex flex-col">
                    {/* Header (title + close X), flush inside the popover so the
                        border wraps the whole menu top and bottom — mirrors the
                        command menu. items-stretch makes the X fill the header
                        height and the rounded top-right corner. */}
                    <div className="flex items-stretch border-b border-divider">
                      <div className="flex flex-1 min-w-0 items-center px-3 py-2">
                        <span className="text-xs font-semibold text-default-600">
                          {t("chatInput.addMenu.label")}
                        </span>
                      </div>
                      <button
                        aria-label={t("Close")}
                        className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                        data-id="add-menu-main-close"
                        type="button"
                        onClick={() => handleAddMenuOpenChange(false)}
                      >
                        <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
                      </button>
                    </div>

                    {/* Body — padding keeps the rounded hover pills off the edges.
                        Items use an inset ring (not an offset outline) so the focus
                        state is never clipped by the popover's overflow-hidden. */}
                    <div className="flex flex-col gap-0.5 p-1">
                      {/* Attach file */}
                      <button
                        data-add-menu-nav
                        className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700 hover:bg-default-100 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                        data-id="add-menu-attach-file"
                        type="button"
                        onClick={handleAttachFile}
                      >
                        <Icon
                          className="text-default-500 shrink-0"
                          icon="solar:paperclip-linear"
                          width={16}
                        />
                        <span>{t("chatInput.addMenu.attachFile")}</span>
                      </button>

                      {/* Mention people or workflows */}
                      <button
                        data-add-menu-nav
                        className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700 hover:bg-default-100 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                        data-id="add-menu-mention"
                        type="button"
                        onClick={handleMention}
                      >
                        <span
                          aria-hidden="true"
                          className="flex w-4 shrink-0 items-center justify-center text-base font-semibold leading-none text-default-500"
                        >
                          @
                        </span>
                        <span>{t("chatInput.addMenu.mention")}</span>
                      </button>

                      {/* Connectors — opens the connectors sub-view */}
                      <button
                        data-add-menu-nav
                        aria-haspopup="menu"
                        className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700 hover:bg-default-100 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                        data-id="add-menu-connectors"
                        type="button"
                        onClick={handleOpenConnectors}
                      >
                        <Icon
                          className="text-default-500 shrink-0"
                          icon="solar:plug-circle-linear"
                          width={16}
                        />
                        <span className="flex-1">{t("chatInput.addMenu.connectors")}</span>
                        <Icon
                          className="text-default-400 shrink-0"
                          icon="solar:alt-arrow-right-linear"
                          width={16}
                        />
                      </button>

                      {/* Memory — toggles the chat agent's User Memory */}
                      {chatAgentEntry && (
                        <div
                          className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700"
                          data-id="add-menu-memory"
                        >
                          <Icon
                            className="text-default-500 shrink-0"
                            icon="solar:database-linear"
                            width={16}
                          />
                          <div className="flex flex-1 flex-col min-w-0">
                            <span>{t("chatInput.addMenu.memory")}</span>
                            <span className="text-[11px] text-default-400 truncate">
                              {t("Remember facts about you for personalised responses")}
                            </span>
                          </div>
                          <Switch
                            aria-label={t("Toggle User Memory")}
                            data-id="add-menu-memory-toggle"
                            isDisabled={!!runId}
                            isSelected={isMemoryEnabled}
                            size="sm"
                            onChange={handleMemoryToggle}
                          >
                            <Switch.Content>
                              <Switch.Control>
                                <Switch.Thumb />
                              </Switch.Control>
                            </Switch.Content>
                          </Switch>
                        </div>
                      )}
                    </div>
                  </div>
                ) : (
                  <div className="flex flex-col">
                    {/* Connectors sub-view header: back navigation + close (X),
                        flush inside the popover border to match the main view and
                        the command menu. */}
                    <div className="flex items-stretch border-b border-divider">
                      <button
                        className="flex flex-1 min-w-0 items-center gap-2 px-3 py-2 text-left hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                        data-id="add-menu-connectors-back"
                        type="button"
                        onClick={() => setAddMenuView("main")}
                      >
                        <Icon
                          className="w-4 h-4 text-default-500 shrink-0"
                          icon="solar:alt-arrow-left-linear"
                        />
                        <span className="text-xs font-semibold text-default-600 truncate">
                          {t("chatInput.addMenu.connectors")}
                        </span>
                      </button>
                      <button
                        aria-label={t("Close")}
                        className="flex shrink-0 items-center justify-center px-3 min-h-11 text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                        data-id="add-menu-connectors-close"
                        type="button"
                        onClick={() => handleAddMenuOpenChange(false)}
                      >
                        <Icon className="w-4 h-4" icon="solar:close-circle-linear" />
                      </button>
                    </div>

                    {/* Body */}
                    <div className="flex flex-col gap-0.5 p-1">
                      {/* Connector list — driven by the real OAuth services */}
                      {SERVICES.map((service) => {
                        const status = getToolConnectionStatus(service.id);
                        const isConnected = status?.connected ?? false;
                        const isConnecting = connectingToolId === service.id;

                        return (
                          <button
                            key={service.id}
                            data-add-menu-nav
                            className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700 hover:bg-default-100 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset disabled:cursor-default disabled:hover:bg-transparent"
                            data-id={`add-menu-connector-${service.id}`}
                            disabled={isConnected || isConnecting}
                            type="button"
                            onClick={() => handleConnectorPress(service.id)}
                          >
                            <Icon className="shrink-0" icon={service.icon} width={18} />
                            <span className="flex-1 truncate">{t(service.name)}</span>
                            {isConnecting ? (
                              <Spinner
                                aria-label={t("Connecting...")}
                                data-id={`add-menu-connector-${service.id}-spinner`}
                                size="sm"
                              />
                            ) : isConnected ? (
                              <span className="text-tiny text-default-400 shrink-0">
                                {t("Connected")}
                              </span>
                            ) : (
                              <span className="text-tiny text-primary-600 shrink-0">
                                {t("Connect")}
                              </span>
                            )}
                          </button>
                        );
                      })}

                      <div className="my-1 h-px bg-default-200" />

                      {/* Manage connectors — full settings page */}
                      <button
                        data-add-menu-nav
                        className="flex items-center gap-2 w-full px-3 py-2 rounded-md text-sm text-default-700 hover:bg-default-100 transition-colors text-left focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                        data-id="add-menu-manage-connectors"
                        type="button"
                        onClick={handleManageConnectors}
                      >
                        <Icon
                          className="text-default-500 shrink-0"
                          icon="solar:settings-linear"
                          width={16}
                        />
                        <span>{t("chatInput.addMenu.manageConnectors")}</span>
                      </button>
                    </div>
                  </div>
                )}

                {/* Footer — keyboard hints + settings cog, matching the command menu */}
                <div className="flex items-center justify-between gap-2 border-t border-divider px-3 py-2 text-xs text-default-400">
                  <div className="flex shrink-0 items-center gap-3">
                    <span className="flex items-center gap-1">
                      <kbd className="rounded bg-default-100 px-1 py-0.5 font-mono text-[10px]">
                        ↑↓
                      </kbd>
                      {t("chatCommandMenu.footer.navigate")}
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="rounded bg-default-100 px-1 py-0.5 font-mono text-[10px]">
                        Enter
                      </kbd>
                      {t("chatCommandMenu.footer.select")}
                    </span>
                    <span className="flex items-center gap-1">
                      <kbd className="rounded bg-default-100 px-1 py-0.5 font-mono text-[10px]">
                        Esc
                      </kbd>
                      {t("chatCommandMenu.footer.close")}
                    </span>
                  </div>
                  <Tooltip>
                    <TooltipTrigger>
                      <button
                        aria-label={t("chatCommandMenu.footer.manageInSettings")}
                        className="flex shrink-0 items-center rounded text-primary hover:opacity-80 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-primary-500 focus-visible:ring-inset"
                        type="button"
                        onClick={handleOpenChatInputSettings}
                      >
                        <Icon className="w-4 h-4" icon="solar:settings-linear" />
                      </button>
                    </TooltipTrigger>
                    <TooltipContent showArrow>
                      {t("chatCommandMenu.footer.manageInSettings")}
                    </TooltipContent>
                  </Tooltip>
                </div>
              </PopoverContent>
            </Popover>
          )}

          {/* "/" slash affordance — opens the command menu */}
          <button
            aria-expanded={commandMenuVisible}
            aria-haspopup="menu"
            aria-label={t("chatInput.commandMenu.open")}
            className="inline-flex items-center justify-center w-7 h-7 rounded-md text-default-400 hover:text-default-600 hover:bg-default-100 transition-colors text-sm font-medium focus-visible:outline-2 focus-visible:outline-offset-2 focus-visible:outline-primary"
            data-id="slash-affordance-button"
            type="button"
            onClick={onCommandMenuOpen}
          >
            /
          </button>

          {/* ── Inline controls (visibility governed by isControlVisible) ── */}

          {/* Web Search switch */}
          {isControlVisible("search", showSearch) && (
            <Tooltip>
              <TooltipTrigger>
                <div className="flex items-center gap-2 py-1 px-2">
                  <Icon className="text-default-500" icon="solar:global-linear" width={18} />
                  <span className="text-tiny text-default-500">{t("Web Search")}</span>
                  <Switch
                    aria-label={t("Web Search")}
                    color={isSearchEnabled ? "success" : "default"}
                    isSelected={isSearchEnabled}
                    size="sm"
                    onChange={onWebSearchToggle}
                  >
                    <Switch.Content>
                      <Switch.Control>
                        <Switch.Thumb />
                      </Switch.Control>
                    </Switch.Content>
                  </Switch>
                </div>
              </TooltipTrigger>
              <TooltipContent showArrow>
                {isSearchEnabled ? t("Disable web search") : t("Enable web search")}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Knowledge Hub toggle */}
          {isControlVisible("knowledgeHub", showKnowledgeHub) && (
            <Tooltip>
              <TooltipTrigger>
                <Button
                  aria-label={t("Knowledge Hub")}
                  aria-pressed={isKnowledgeHubEnabled}
                  className={cn(
                    "shrink-0 min-w-fit rounded-full border!",
                    isKnowledgeHubEnabled
                      ? "bg-primary-100 hover:bg-primary-200 border-primary-500! text-primary-700"
                      : "bg-default-100 hover:bg-default-200 border-default-300!",
                  )}
                  data-id="knowledge-hub-button"
                  size="sm"
                  variant="ghost"
                  onPress={() => {
                    onKnowledgeHubToggle();
                    setTimeout(() => {
                      blurActiveElement();
                    }, 0);
                  }}
                >
                  <Icon
                    className={isKnowledgeHubEnabled ? "text-primary-700" : "text-default-500"}
                    icon="solar:database-linear"
                    width={18}
                  />
                  <span className="hidden sm:inline">{t("Knowledge Hub")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent showArrow>
                {isKnowledgeHubEnabled ? t("Disable knowledge hub") : t("Enable knowledge hub")}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Browse prompts */}
          {isControlVisible("prompts", showPrompts) && (
            <Tooltip>
              <TooltipTrigger>
                <Button
                  aria-label={t("Prompts")}
                  className="shrink-0 min-w-fit bg-default-100 hover:bg-default-200 border! border-default-300! rounded-full disabled:bg-default-200 disabled:text-default-400 disabled:cursor-not-allowed"
                  data-id="prompts-button"
                  disabled={isPromptLocked}
                  size="sm"
                  variant="ghost"
                  onPress={() => {
                    if (!isPromptLocked) {
                      onBrowsePrompts();
                    }
                  }}
                >
                  <Icon
                    className={isPromptLocked ? "text-default-400" : "text-default-500"}
                    icon="solar:code-bold"
                    width={18}
                  />
                  <span className="hidden sm:inline">{t("Prompts")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent showArrow>
                {isPromptLocked
                  ? t("Prompts disabled for this workflow")
                  : t("Browse and select from saved prompts")}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Prompto */}
          {isControlVisible("prompto", showPrompto) && (
            <Tooltip>
              <TooltipTrigger>
                <Button
                  aria-label={t("Prompto")}
                  className="shrink-0 min-w-fit bg-default-100 hover:bg-default-200 border! border-default-300! rounded-full"
                  data-id="prompto-button"
                  size="sm"
                  variant="ghost"
                  onPress={onPrompto}
                >
                  <Icon className="shrink-0" icon="solar:magic-stick-3-bold" width={16} />
                  <span className="hidden sm:inline">{t("Prompto")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent showArrow>{t("Generate effective prompts with AI")}</TooltipContent>
            </Tooltip>
          )}

          {/* Configuration */}
          {isControlVisible("configuration", showSettings) && (
            <Tooltip>
              <TooltipTrigger>
                <Button
                  aria-label={t("Configuration")}
                  className="shrink-0 min-w-fit bg-default-100 hover:bg-default-200 border! border-default-300! rounded-full disabled:bg-default-200 disabled:text-default-400 disabled:cursor-not-allowed"
                  data-id="configuration-button"
                  disabled={isConfigurationDisabled}
                  size="sm"
                  variant="ghost"
                  onPress={() => {
                    if (!isConfigurationDisabled) {
                      onOpenConfiguration();
                      setTimeout(() => {
                        blurActiveElement();
                      }, 0);
                    }
                  }}
                >
                  <Icon
                    className={isConfigurationDisabled ? "text-default-400" : "text-default-500"}
                    icon="solar:settings-linear"
                    width={18}
                  />
                  <span className="hidden sm:inline">{t("Configuration")}</span>
                </Button>
              </TooltipTrigger>
              <TooltipContent showArrow>
                {isConfigurationDisabled
                  ? t("disabled-model-selector")
                  : t("Open chat configuration settings")}
              </TooltipContent>
            </Tooltip>
          )}
        </div>

        {/* RIGHT: active chips (optional), model selector, mic, send ──────── */}
        <div className="flex items-center gap-2 shrink-0 flex-wrap justify-end">
          {/* Active-feature indicators — icon-only, shown when the "Show active feature indicators" setting is on */}
          {showActiveFeatureChips && (isSearchEnabled || isKnowledgeHubEnabled) && (
            <div className="flex items-center gap-1" data-id="active-features-chips">
              {isSearchEnabled && (
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      isIconOnly
                      aria-label={t("chatInput.chips.webSearchTooltip")}
                      className="w-6 h-6 min-w-6 rounded-full border bg-green-50 text-green-700 border-green-200 hover:bg-green-100 dark:bg-green-500/15 dark:text-green-300 dark:border-green-400/40 dark:hover:bg-green-500/25"
                      data-id="web-search-chip"
                      size="sm"
                      variant="ghost"
                      onPress={() => onWebSearchToggle(false)}
                    >
                      <Icon icon="solar:global-linear" width={12} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent showArrow className="max-w-70" placement="top">
                    {t("chatInput.chips.webSearchTooltip")}
                  </TooltipContent>
                </Tooltip>
              )}

              {isKnowledgeHubEnabled && (
                <Tooltip>
                  <TooltipTrigger>
                    <Button
                      isIconOnly
                      aria-label={t("chatInput.chips.knowledgeHubTooltip")}
                      className="w-6 h-6 min-w-6 rounded-full border bg-primary-50 text-primary-600 border-primary-200 hover:bg-primary-100 dark:bg-primary-500/15 dark:text-primary-300 dark:border-primary-500/40 dark:hover:bg-primary-500/25"
                      data-id="knowledge-hub-chip"
                      size="sm"
                      variant="ghost"
                      onPress={onKnowledgeHubToggle}
                    >
                      <Icon icon="solar:database-linear" width={12} />
                    </Button>
                  </TooltipTrigger>
                  <TooltipContent showArrow className="max-w-70" placement="top">
                    {t("chatInput.chips.knowledgeHubTooltip")}
                  </TooltipContent>
                </Tooltip>
              )}
            </div>
          )}

          {/* Model selector — visible by default (modelSelector pinned true), hideable via Settings */}
          {isControlVisible("modelSelector", showChatModelSelector) && <ChatModelSelector />}
          {/* Voice recording */}
          {showVoice && browserSupportsSpeechRecognition && (
            <Tooltip>
              <TooltipTrigger>
                <Button
                  isIconOnly
                  aria-label={
                    isPromptLocked
                      ? t("Voice input disabled for this workflow")
                      : listening
                        ? t("Stop voice recording")
                        : t("Start voice recording")
                  }
                  className="rounded-full border border-default-300 bg-default-100 hover:bg-default-200 disabled:bg-default-200 disabled:text-default-400 disabled:cursor-not-allowed"
                  color={listening ? "danger" : "default"}
                  data-id="voice-button"
                  disabled={isPromptLocked}
                  size="sm"
                  variant="ghost"
                  onPress={onVoicePress}
                >
                  <Icon
                    className={isPromptLocked ? "text-default-400" : "text-default-500"}
                    icon={listening ? "solar:stop-circle-linear" : "solar:microphone-linear"}
                    width={18}
                  />
                </Button>
              </TooltipTrigger>
              <TooltipContent showArrow>
                {isPromptLocked
                  ? t("Voice input disabled for this workflow")
                  : listening
                    ? t("Stop voice recording")
                    : t("Start voice recording")}
              </TooltipContent>
            </Tooltip>
          )}

          {/* Speech error */}
          {speechError && (
            <div className="text-danger text-sm" role="alert">
              {speechError}
            </div>
          )}

          {/* Send / Stop button */}
          <Tooltip>
            <TooltipTrigger>
              <Button
                isIconOnly
                aria-label={streaming ? t("Stop streaming") : t("Send message")}
                className="rounded-full"
                color={streaming ? "danger" : !prompt ? "default" : undefined}
                data-id={streaming ? "stop-streaming-button" : "send-message-button"}
                isDisabled={streaming ? false : !prompt || isUploading || isUploadingFiles}
                size="sm"
                style={sendButtonStyle}
                type={streaming ? "button" : "submit"}
                variant="primary"
                onPress={streaming ? onStop : undefined}
              >
                <Icon
                  className={cn(
                    "[&>path]:stroke-[2px]",
                    streaming
                      ? "text-danger-foreground"
                      : !prompt
                        ? "text-default-600"
                        : // ready: inherit the button's contrast-safe foreground
                          undefined,
                  )}
                  icon={streaming ? "solar:stop-circle-linear" : "solar:arrow-up-linear"}
                  width={20}
                />
              </Button>
            </TooltipTrigger>
            <TooltipContent showArrow>
              {streaming ? t("Stop streaming") : t("Send message")}
            </TooltipContent>
          </Tooltip>
        </div>
      </div>

      {/* ── Hidden file input ────────────────────────────────────────────── */}
      <input
        ref={fileInputRef}
        multiple
        accept={allowedFileTypes.join(",")}
        data-id="upload-file-input"
        style={{ display: "none" }}
        type="file"
        onChange={onFileChange}
      />

      {/* ── ChatCommandMenu portal ───────────────────────────────────────── */}
      <ChatCommandMenu
        activeWorkflowId={workflow?.id ?? null}
        isConfigurationDisabled={isConfigurationDisabled}
        isKnowledgeHubEnabled={isKnowledgeHubEnabled}
        isPromptLocked={isPromptLocked}
        isWebSearchEnabled={isSearchEnabled}
        modelSelectorEnabled={pinnedChatControls.modelSelector}
        position={commandMenuPosition}
        showConfiguration={showSettings}
        showKnowledgeHub={showKnowledgeHub}
        showModelSelector={showChatModelSelector}
        showPrompto={showPrompto}
        showPrompts={showPrompts}
        showWebSearch={showSearch}
        showWorkflows={workflows.length > 0}
        skillQuery={prompt.startsWith("/") ? prompt.slice(1) : ""}
        skills={skills}
        visible={commandMenuVisible}
        workflows={workflows}
        workflowsLabel={workflowsLabel}
        onBrowseAllWorkflows={handleBrowseAllWorkflows}
        onBrowsePrompts={onBrowsePrompts}
        onClose={onCommandMenuClose}
        onCreateWorkflow={canCreateWorkflows ? handleCreateWorkflow : undefined}
        onKnowledgeHubToggle={onKnowledgeHubToggle}
        onModelSelectorToggle={(val) => setPinnedChatControl("modelSelector", val)}
        onOpenConfiguration={onOpenConfiguration}
        onPrompto={onPrompto}
        onSelectSkill={onSelectSkill}
        onSelectWorkflow={handleSelectWorkflow}
        onWebSearchToggle={onWebSearchToggle}
      />
    </div>
  );
};
